import { Button, Divider } from '@design-system';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { Trans, useTranslation } from 'next-i18next';
import { useState } from 'react';

import { FF_FRAMING_GUARANTEE_COPY } from '../../../../constants/featureFlags';
import { Frame } from '../../../../types/Products';
import Drawer from '../../../Layout/Drawer/Drawer';
import { useFrameDraft } from '../hooks/useFrameDraft';
import { CurrencyCode, FramingChoice, FramingProduct } from '../types';
import { getFramePrice, getFramingSubtotal } from '../utils/framePricing';
import { getFramingChanges } from '../utils/framingChanges';
import { ApplyToAllCheckbox } from './ApplyToAllCheckbox';
import { FrameStylePicker } from './FrameStylePicker';
import { FramingChanges } from './FramingChanges';
import {
  FramingGuarantee,
  isFramingGuaranteeVariant,
} from './FramingGuarantee';
import { FramingSubtotal } from './FramingSubtotal';
import { GlazingPicker } from './GlazingPicker';
import { MountingPicker } from './MountingPicker';

export interface FramingDrawerProps {
  shown: boolean;
  /** The product being framed; when null the drawer renders closed. */
  product: FramingProduct | null;
  /** The product's current confirmed selection, used to seed the draft. */
  currentSelection: Frame | null | undefined;
  orderedFrame?: Frame | null;
  showApplyToAll: boolean;
  defaultApplyToAll?: boolean;
  currencyCode?: CurrencyCode;
  onClose: () => void;
  /** Commit the choice; `null` choice means "continue unframed". */
  onConfirm: (choice: FramingChoice, applyToAll: boolean) => void;
}

/**
 * The framing drawer: seeds a draft from the current selection, composes the
 * stateless pickers, and commits a choice. The draft lives in `useFrameDraft`;
 * this container only wires it to the UI and the side drawer.
 */
export const FramingDrawer = ({
  currencyCode,
  currentSelection,
  defaultApplyToAll = false,
  onClose,
  onConfirm,
  orderedFrame,
  product,
  showApplyToAll,
  shown,
}: FramingDrawerProps) => {
  if (!shown || !product) {
    return <Drawer onClose={onClose} shown={false} />;
  }

  return (
    <Drawer onClose={onClose} shown={shown}>
      <FramingDrawerBody
        key={product.id}
        currencyCode={currencyCode}
        currentSelection={currentSelection}
        defaultApplyToAll={defaultApplyToAll}
        onConfirm={onConfirm}
        orderedFrame={orderedFrame}
        product={product}
        showApplyToAll={showApplyToAll}
      />
    </Drawer>
  );
};

type FramingDrawerBodyProps = Omit<FramingDrawerProps, 'shown' | 'onClose'> & {
  product: FramingProduct;
};

/**
 * Rendered only while open (and remounted per product via `key`), so the draft
 * hook re-seeds naturally without reset effects.
 */
const FramingDrawerBody = ({
  currencyCode,
  currentSelection,
  defaultApplyToAll = false,
  onConfirm,
  orderedFrame,
  product,
  showApplyToAll,
}: FramingDrawerBodyProps) => {
  const { t } = useTranslation('checkout');
  const [applyToAll, setApplyToAll] = useState(defaultApplyToAll);
  const guaranteeFlagValue = useFeatureValue<string>(
    FF_FRAMING_GUARANTEE_COPY,
    'control',
  );

  const draft = useFrameDraft({
    frameOptions: product.frameOptions,
    currentSelection,
  });

  const subtotal = getFramingSubtotal(product.subtotal, draft.draftFrame);
  const frameGroupName = `frame-style-${product.id}`;
  const glazingGroupName = `glazing-${product.id}`;
  const mountingGroupName = `mounting-${product.id}`;

  const isOrderUpdate = orderedFrame !== undefined;

  return (
    <div className="grid w-full gap-9">
      <h2 className="text-md font-bold md:text-lg">
        {t('framing.drawerTitle')}
      </h2>

      <FrameStylePicker
        currencyCode={currencyCode}
        isFloat={isOrderUpdate}
        groupName={frameGroupName}
        onSelect={draft.selectStyle}
        options={product.frameOptions}
        selectedTitle={draft.selectedStyleTitle}
      />

      <MountingPicker
        currencyCode={currencyCode}
        isFloat={isOrderUpdate}
        groupName={mountingGroupName}
        onSelect={draft.selectMounting}
        options={draft.mountingOptions}
        selectedMounting={draft.selectedMounting}
      />

      <GlazingPicker
        currencyCode={currencyCode}
        isFloat={isOrderUpdate}
        groupName={glazingGroupName}
        onSelect={draft.selectGlazing}
        options={draft.glazingOptions}
        selectedGlazing={draft.selectedGlazing}
      />

      {isOrderUpdate ? (
        <FramingChanges
          changes={getFramingChanges(
            product.frameOptions,
            orderedFrame,
            draft.draftFrame,
          )}
          currencyCode={currencyCode}
          total={getFramePrice(draft.draftFrame) - getFramePrice(orderedFrame)}
        />
      ) : (
        <FramingSubtotal amount={subtotal} currencyCode={currencyCode} />
      )}

      {showApplyToAll ? (
        <ApplyToAllCheckbox checked={applyToAll} onChange={setApplyToAll} />
      ) : null}

      {isFramingGuaranteeVariant(guaranteeFlagValue) ? (
        <FramingGuarantee variant={guaranteeFlagValue} />
      ) : null}

      <div className="grid gap-3">
        <Button
          fullWidth
          label={t('framing.confirm')}
          onClick={() => onConfirm(draft.choice, applyToAll)}
          variant="primary"
        />
        {orderedFrame ? null : (
          <Button
            fullWidth
            label={t('framing.continueUnframed')}
            onClick={() => onConfirm(null, applyToAll)}
            variant="tertiary"
          />
        )}
      </div>

      <Divider />

      <p className="text-balance text-center text-neutral-4">
        <Trans
          components={{
            studio: (
              <a
                className="hyperlink"
                href="https://avantarte.com/insights/guides/our-framing-studio"
                rel="noopener noreferrer"
                target="_blank"
              />
            ),
          }}
          i18nKey="framing.note"
          ns="checkout"
          t={t}
        />
      </p>
    </div>
  );
};

export default FramingDrawer;
